import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAppDispatch } from "../../redux/store"
import {
  clearEventId,
  clearQrId,
} from "../../redux/slices/events/eventsSlice"
import { IEvent } from "../../components/Events/types"
import { groupEvents } from "./useEventGroups"
import styles from "./EventsPage.module.scss"

interface EventsHeaderProps {
  events: IEvent[]
  total: number
}

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path
      d="M7 2.5V11.5M2.5 7H11.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

// Page-level header. Two jobs:
//   1. Identity — one word: "Events". No redundant "All Created" prefix.
//   2. Calibration — one sentence that tells the operator what's on their
//      plate ("47 events · 3 happening soon"). Replaces the old dead space
//      between title and list with something actionable.
const EventsHeader = ({ events, total }: EventsHeaderProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const groups = groupEvents(events)
  const summary = buildSummary(total, groups.upcoming.length)

  const handleCreate = () => {
    dispatch(clearQrId())
    dispatch(clearEventId())
    navigate("/event-create")
  }

  return (
    <div className={styles.header}>
      <div className={styles.headerTitleBlock}>
        <h1 className={styles.headerTitle}>{t("events.events")}</h1>
        {summary && <p className={styles.headerSummary}>{summary}</p>}
      </div>

      <div className={styles.headerActions}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={handleCreate}
        >
          <PlusIcon />
          {t("buttons.createEvent")}
        </button>
      </div>
    </div>
  )
}

// Build the human subtitle. Total anchors the sentence so scale is obvious;
// upcoming count communicates the near-term workload. Deliberately plain
// English, no commas-as-separators clutter.
const buildSummary = (
  total: number,
  upcoming: number,
): string | null => {
  if (total === 0) return null

  const parts: string[] = []
  parts.push(`${total} ${total === 1 ? "event" : "events"}`)

  if (upcoming > 0) {
    parts.push(
      `${upcoming} upcoming ${upcoming === 1 ? "event" : "events"}`,
    )
  }

  return parts.join(" · ")
}

export default EventsHeader
